import { z } from "zod";
import { DrinkSchema, IngredientSchema } from "@/api/types/drink";

/**
 * Event menu rows (Phase 4). A row is either a library drink reference
 * (`drink_id` set, `is_custom` false) or an event-only custom drink
 * (`is_custom` true, `custom_name`/`custom_story`/`custom_ingredients` set,
 * `drink_id` null) — per the Phase 4 contract's MenuRow shape.
 */

export const MenuRowSchema = z.object({
  id: z.string(),
  event_id: z.string(),
  drink_id: z.string().nullable(),
  is_custom: z.boolean(),
  custom_name: z.string().nullable(),
  custom_story: z.string().nullable(),
  custom_ingredients: z.array(IngredientSchema).nullable(),
  serve_na: z.boolean(),
  sort_order: z.number(),
  // The API resolves a unified display shape onto every row (library OR
  // custom): flat `name`/`story`/`ingredients`. Prefer these.
  name: z.string().optional(),
  story: z.string().nullable().optional(),
  ingredients: z.array(IngredientSchema).optional(),
  // Legacy/optional nested form; the current API sends flat fields above.
  drink: DrinkSchema.optional(),
});
export type MenuRow = z.infer<typeof MenuRowSchema>;

export const MenuListResponseSchema = z.object({
  items: z.array(MenuRowSchema),
});
export type MenuListResponse = z.infer<typeof MenuListResponseSchema>;

/** Display name for a row regardless of library/custom kind. */
export function menuRowName(row: MenuRow): string {
  if (row.name) return row.name;
  if (row.is_custom) return row.custom_name ?? "Custom drink";
  return row.drink?.name ?? "Drink";
}

export function menuRowStory(row: MenuRow): string | null {
  if (row.story !== undefined && row.story !== null) return row.story;
  if (row.is_custom) return row.custom_story;
  return row.drink?.story ?? null;
}

// ---- Mutation inputs ----------------------------------------------------

export interface AddMenuRowInput {
  eventId: string;
  drink_id?: string;
  custom_name?: string;
  custom_story?: string | null;
  custom_ingredients?: Array<{ name: string; oz_per_serving: number; category: string }>;
  serve_na?: boolean;
  sort_order?: number;
}

export interface PatchMenuRowInput {
  eventId: string;
  rowId: string;
  serve_na?: boolean;
  sort_order?: number;
  custom_name?: string;
  custom_story?: string | null;
}

export interface DeleteMenuRowInput {
  eventId: string;
  rowId: string;
}

export interface GenerateMenuPdfResult {
  pdf_key?: string | null;
  url?: string;
}

export const GenerateMenuPdfResultSchema = z.object({
  pdf_key: z.string().nullable().optional(),
  url: z.string().optional(),
});
